import { create } from 'zustand';
import { createInitialState, gameReducer } from '../domain/gameEngine';
import type { GameAction, GameState } from '../domain/types';

export type CameraMode = 'play' | 'overview';

const BOT_SPEED_STORAGE_KEY = 'mdp-bot-speed';
const DEFAULT_BOT_SPEED_MS = 850;
const MIN_BOT_SPEED_MS = 300;
const MAX_BOT_SPEED_MS = 2500;

function loadBotSpeedMs(): number {
  if (typeof window === 'undefined') return DEFAULT_BOT_SPEED_MS;
  const stored = localStorage.getItem(BOT_SPEED_STORAGE_KEY);
  if (!stored) return DEFAULT_BOT_SPEED_MS;
  const parsed = Number(stored);
  if (Number.isNaN(parsed)) return DEFAULT_BOT_SPEED_MS;
  return Math.min(MAX_BOT_SPEED_MS, Math.max(MIN_BOT_SPEED_MS, parsed));
}

export const BOT_SPEED_PRESETS = {
  slow: 1500,
  normal: 850,
  fast: 400,
} as const;

export { MIN_BOT_SPEED_MS, MAX_BOT_SPEED_MS, DEFAULT_BOT_SPEED_MS };

interface GameStore {
  game: GameState;
  cameraMode: CameraMode;
  botSpeedMs: number;
  botActingPlayerIndex: number | null;
  settingsOpen: boolean;
  missionsMenuOpen: boolean;
  dispatch: (action: GameAction) => void;
  setCameraMode: (mode: CameraMode) => void;
  setBotSpeedMs: (ms: number) => void;
  setBotActingPlayerIndex: (index: number | null) => void;
  setSettingsOpen: (open: boolean) => void;
  setMissionsMenuOpen: (open: boolean) => void;
}

export const useGameStore = create<GameStore>((set) => ({
  game: createInitialState(),
  cameraMode: 'play',
  botSpeedMs: loadBotSpeedMs(),
  botActingPlayerIndex: null,
  settingsOpen: false,
  missionsMenuOpen: false,
  dispatch: (action) =>
    set((state) => ({
      game: gameReducer(state.game, action),
    })),
  setCameraMode: (mode) => set({ cameraMode: mode }),
  setBotSpeedMs: (ms) => {
    const clamped = Math.min(MAX_BOT_SPEED_MS, Math.max(MIN_BOT_SPEED_MS, ms));
    localStorage.setItem(BOT_SPEED_STORAGE_KEY, String(clamped));
    set({ botSpeedMs: clamped });
  },
  setBotActingPlayerIndex: (index) => set({ botActingPlayerIndex: index }),
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  setMissionsMenuOpen: (open) => set({ missionsMenuOpen: open }),
}));
